import { imgUrl, imgSrcSet } from '../lib/img';
import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Star, Heart, Plus, Minus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { Button } from './ui/button';

export default function ProductCard({ product }) {
  const { addItem, items, updateQty, remove } = useCart();
  const { has, toggle } = useWishlist();
  const wished = has(product.id);
  const inCart = items.find((i) => i.product_id === product.id);
  const discount =
    product.compare_at_price && product.compare_at_price > product.price
      ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
      : 0;

  const handleDec = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!inCart) return;
    if (inCart.quantity <= 1) remove(product.id);
    else updateQty(product.id, inCart.quantity - 1);
  };
  const handleInc = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, 1);
  };

  return (
    <div data-testid={`product-card-${product.slug}`} className="card-lift gold-foil-edge relative bg-white rounded-xl sm:rounded-2xl overflow-hidden flex flex-col">
      <Link to={`/product/${product.slug}`} className="block relative img-zoom bg-brand-ivory">
        <img
          src={imgUrl(product.images && product.images[0], 640)}
          srcSet={imgSrcSet(product.images && product.images[0], 1024)}
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          alt={product.name}
          className="w-full aspect-square object-cover"
          loading="lazy"
          decoding="async"
        />
        {discount > 0 && (
          <span className="absolute top-11 left-2 sm:top-[3.25rem] sm:left-3 bg-brand-dark text-brand-gold text-[10px] sm:text-xs font-medium px-1.5 sm:px-2 py-0.5 sm:py-1 rounded" data-testid="product-discount-badge">
            -{discount}%
          </span>
        )}
        {product.is_best_seller && (
          <span className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-brand-gold text-brand-dark text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
            Best
          </span>
        )}
      </Link>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(product); }}
        aria-label={wished ? `Remove ${product.name} from wishlist` : `Save ${product.name} to wishlist`}
        aria-pressed={wished}
        className={`absolute top-2 left-2 sm:top-3 sm:left-3 z-10 w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-colors backdrop-blur-sm ${wished ? 'bg-brand-gold text-brand-dark' : 'bg-white/85 text-brand-dark hover:bg-white'} focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold`}
      >
        <Heart size={15} className={wished ? 'fill-brand-dark' : ''} aria-hidden="true" />
      </button>
      <div className="p-3 sm:p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-1 text-[10px] sm:text-xs text-brand-green/70 mb-1">
          <Star size={11} className="fill-brand-gold text-brand-gold" />
          <span>{product.rating?.toFixed(1) ?? '4.5'}</span>
          <span className="text-brand-green/40 hidden sm:inline">• {product.reviews_count || 0} reviews</span>
        </div>
        <Link to={`/product/${product.slug}`} className="block">
          <h3 className="font-serif-brand text-sm sm:text-lg text-brand-dark leading-snug hover:text-brand-green transition-colors line-clamp-2 min-h-[2.5rem] sm:min-h-[3.5rem]">
            {product.name}
          </h3>
        </Link>
        <p className="text-[11px] sm:text-xs text-brand-green/70 mt-1 line-clamp-2 hidden sm:block min-h-[2rem]">{product.description}</p>
        <div className="mt-auto pt-2 sm:pt-3">
          <div className="flex items-baseline gap-2">
            <span className="text-base sm:text-lg font-semibold text-brand-dark">₹{product.price.toFixed(0)}</span>
            {discount > 0 && (
              <span className="text-xs sm:text-sm line-through text-brand-green/50">₹{product.compare_at_price.toFixed(0)}</span>
            )}
          </div>
          {!inCart && (
            <Button
              onClick={handleInc}
              className="btn-gold mt-3 w-full h-9 sm:h-10 rounded-full text-xs sm:text-sm font-medium"
              data-testid={`add-to-cart-${product.slug}`}
            >
              <ShoppingBag size={14} className="mr-1.5 sm:mr-2" /> Add to Cart
            </Button>
          )}
          {inCart && (
            <div
              className="mt-3 w-full h-9 sm:h-10 rounded-full flex items-center justify-between px-1 bg-[#3d8a5c] text-white select-none"
              data-testid={`qty-controls-${product.slug}`}
            >
              <button
                type="button"
                onClick={handleDec}
                aria-label={`Decrease quantity of ${product.name}`}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/15 active:bg-white/25 transition-colors"
              >
                <Minus size={14} strokeWidth={2.5} />
              </button>
              <span className="text-sm font-semibold" aria-live="polite">{inCart.quantity}</span>
              <button
                type="button"
                onClick={handleInc}
                aria-label={`Increase quantity of ${product.name}`}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/15 active:bg-white/25 transition-colors"
              >
                <Plus size={14} strokeWidth={2.5} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
